import ShiftEntity, { ShiftStatus } from "../../domain/entities/ShiftEntity";
import StablishmentDto from "./StablishmentDto";
import UserDto from "./UserDto";
import ScheduleDto from "./ScheduleDto";
import DominicanTime from "./DominicanTime";

const fromJSON = (json: any): ShiftEntity => {
    const toDominicanTime = (dateString: string): Date => DominicanTime.toDominicanTime(new Date(dateString));
    const parseStatus = (): ShiftStatus => {
        // Si hay un status explícito en el JSON, usarlo
        if (json['status']) {
            switch (json['status']) {
                case 'pending':
                    return ShiftStatus.PENDING;
                case 'rejected':
                    return ShiftStatus.REJECTED;
                case 'noConfirmed':
                    return ShiftStatus.NO_CONFIRMED;
                case 'waitingFirstConfirmation':
                    return ShiftStatus.WAITING_FIRST_CONFIRMATION;
                case 'waitingSecondConfirmation':
                    return ShiftStatus.WAITING_SECOND_CONFIRMATION;
                case 'confirmedFirst':
                    return ShiftStatus.CONFIRMED_FIRST;
                case 'confirmedSecond':
                    return ShiftStatus.CONFIRMED_SECOND;
                case 'started':
                    return ShiftStatus.STARTED;
                case 'readyToFinish':
                    return ShiftStatus.READY_TO_FINISH;
                case 'finished':
                    return ShiftStatus.FINISHED;
                default:
                    return _getStatusFromLogic();
            }
        }

        // Si no hay status o es null, calcular usando la lógica
        return _getStatusFromLogic();
    }

    const _getStatusFromLogic = (): ShiftStatus => {
        const checkedInAt = json['checked_in_at'] != null
            ? toDominicanTime(json['checked_in_at'])
            : null;
        const checkedOutAt = json['checked_out_at'] != null
            ? toDominicanTime(json['checked_out_at'])
            : null;
        const willAttendFirst = json['will_attend'];
        const willAttendSecond = json['will_attend2'];
        const isReasigned = json['reasigned'];

        // Calcular start y end dates
        const startDate = json['start'] ? toDominicanTime(json['start']) : toDominicanTime(json['startDate']);
        const endDate = json['end'] ? toDominicanTime(json['end']) : null;


        const now = new Date();
        const missingTimeToStart = Math.floor((startDate.getTime() - now.getTime()) / (1000 * 60)); // en minutos
        const missingTimeToEnd = endDate ? Math.floor((endDate.getTime() - now.getTime()) / (1000 * 60)) : 0;

        if (checkedOutAt != null) {
            return ShiftStatus.FINISHED;
        } else if (checkedInAt != null && missingTimeToEnd < 0) {
            return ShiftStatus.READY_TO_FINISH;
        } else if (checkedInAt != null) {
            return ShiftStatus.STARTED;
        } else if (isReasigned === true) {
            return ShiftStatus.NO_CONFIRMED;
        } else if (willAttendSecond === false || willAttendFirst === false) {
            return ShiftStatus.REJECTED;
        } else if (willAttendSecond === true) {
            return ShiftStatus.CONFIRMED_SECOND;
        } else if (willAttendSecond == null && missingTimeToStart < 40) {
            return ShiftStatus.NO_CONFIRMED;
        } else if (willAttendFirst === true && missingTimeToStart > 45) {
            return ShiftStatus.CONFIRMED_FIRST;
        } else if (willAttendFirst === true && missingTimeToStart <= 45) {
            return ShiftStatus.WAITING_SECOND_CONFIRMATION;
        } else if (willAttendFirst == null && missingTimeToStart < 85) {
            return ShiftStatus.NO_CONFIRMED;
        } else if (willAttendFirst === null && missingTimeToStart <= 90) {
            return ShiftStatus.WAITING_FIRST_CONFIRMATION;
        } else {
            return ShiftStatus.PENDING;
        }
    }

    return new ShiftEntity({
        id: json['id']?.toString() ?? '',
        status: parseStatus(),
        turnId: json['turn_id']?.toString() ?? '',
        startDate: json['startDate'] ? toDominicanTime(json['startDate']) : (json['start'] ? toDominicanTime(json['start']) : (json['date'] != null && json['start_time'] != null ? new Date(json['date'].split("T")[0] + " " + json['start_time']) : new Date())),
        endDate: json['endDate'] ? toDominicanTime(json['endDate']) : (json['end'] ? toDominicanTime(json['end']) : (json['date'] != null && json['end_time'] != null ? new Date(json['date'] + " " + json['end_time']) : new Date())),
        stablishment: StablishmentDto.fromJSON(json['stablishment'] || {}),
        user: UserDto.fromJSON(json['user'] || json['valet'] || {}),
        schedule: ScheduleDto.fromJSON(json['schedule'] || {}),
        willAttend: json['will_attend'],
        checkedInAt: json['checked_in_at'] ? toDominicanTime(json['checked_in_at']) : undefined,
        checkedOutAt: json['checked_out_at'] ? toDominicanTime(json['checked_out_at']) : undefined,
        scheduleId: json['schedule_id']?.toString() || (json['schedule'] && json['schedule']['id'] ? json['schedule']['id'].toString() : ''),
    });
}

const toJson = (shift: ShiftEntity): any => {
    const statusString = (): string => {
        switch (shift.status) {
            case ShiftStatus.PENDING:
                return 'pending';
            case ShiftStatus.REJECTED:
                return 'rejected';
            case ShiftStatus.NO_CONFIRMED:
                return 'noConfirmed';
            case ShiftStatus.WAITING_FIRST_CONFIRMATION:
                return 'waitingFirstConfirmation';
            case ShiftStatus.WAITING_SECOND_CONFIRMATION:
                return 'waitingSecondConfirmation';
            case ShiftStatus.CONFIRMED_FIRST:
                return 'confirmedFirst';
            case ShiftStatus.CONFIRMED_SECOND:
                return 'confirmedSecond';
            case ShiftStatus.STARTED:
                return 'started';
            case ShiftStatus.READY_TO_FINISH:
                return 'readyToFinish';
            case ShiftStatus.FINISHED:
                return 'finished';
            default:
                return 'pending';
        }
    }

    return {
        id: shift.id,
        status: statusString(),
        startDate: shift.startDate.toISOString(),
        stablishment: StablishmentDto.toJson(shift.stablishment),
        user: UserDto.toJson(shift.user),
        turn_id: shift.turnId,
        schedule: ScheduleDto.toJson(shift.schedule),
        willAttend: shift.willAttend,
        checkedInAt: shift.checkedInAt?.toISOString(),
        checkedOutAt: shift.checkedOutAt?.toISOString(),
        scheduleId: shift.schedule.id,
    }
}

export default {
    fromJSON,
    toJson,
}